import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import StickyBox from 'react-sticky-box';
import { useSelector } from 'react-redux';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import { Api } from '../../auth/Configs';
import './IntegrityBaselinePage.css';

const endpoint = `${Api}/ged/integrity-baselines`;
const formatDate = (value) => value ? new Date(value).toLocaleString('fr-FR') : '—';
const errorMessage = (error) => error.response?.data?.message || 'Le traitement a été interrompu. Vous pouvez le reprendre.';

export default function IntegrityBaselinePage() {
  const session = useSelector((state) => state.sessionAuth);
  const isAdmin = Array.isArray(session.roles) && session.roles.some((role) => ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN'].includes(role));
  const config = useMemo(() => ({ headers: { Authorization: `Bearer ${session.token}` } }), [session.token]);
  const [overview, setOverview] = useState({ pending: 0, runs: [] });
  const [selectedRun, setSelectedRun] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const active = useRef(true);

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    const { data } = await axios.get(endpoint, config);
    if (active.current) setOverview(data);
  }, [config, isAdmin]);

  useEffect(() => {
    active.current = true;
    if (isAdmin) refresh().catch((requestError) => setError(errorMessage(requestError))).finally(() => setLoading(false));
    else setLoading(false);
    return () => { active.current = false; };
  }, [isAdmin, refresh]);

  const showRun = async (runId) => {
    setError('');
    try {
      const { data } = await axios.get(`${endpoint}/${runId}/entries`, config);
      if (active.current) { setSelectedRun(data.run); setIssues(data.issues); }
    } catch (requestError) { if (active.current) setError(errorMessage(requestError)); }
  };

  const exportRun = async (runId) => {
    setError('');
    try {
      const { data } = await axios.get(`${endpoint}/${runId}/export`, { ...config, responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `integrite-${runId}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (requestError) { setError(errorMessage(requestError)); }
  };

  const processRun = async (runId) => {
    setWorking(true); setError('');
    try {
      let run;
      do {
        const { data } = await axios.post(`${endpoint}/${runId}/step`, {}, config);
        run = data;
        if (!active.current) return;
        setSelectedRun(run);
      } while (run.status === 'running' && active.current);
      await Promise.all([refresh(), showRun(runId)]);
    } catch (requestError) { if (active.current) setError(errorMessage(requestError)); }
    finally { if (active.current) setWorking(false); }
  };

  const start = async () => {
    setWorking(true); setError('');
    setIssues([]);
    try {
      const { data } = await axios.post(endpoint, {}, config);
      if (!active.current) return;
      setSelectedRun(data);
      await refresh();
      if (data.status === 'running') await processRun(data.id);
      else await showRun(data.id);
    } catch (requestError) { if (active.current) setError(errorMessage(requestError)); }
    finally { if (active.current) setWorking(false); }
  };

  const currentRun = overview.runs.find((run) => run.status === 'running');
  return <>
    <Breadcrumb title="Archives" submenu="Régularisation des empreintes" />
    <div className="content"><div className="container-fluid"><div className="row">
      <div className="col-md-4 col-lg-3 col-xl-3 theiaStickySidebar"><StickyBox offsetTop={20} offsetBottom={20}><Sidebar /></StickyBox></div>
      <main className="col-md-8 col-lg-9 col-xl-9 integrity-baseline-page">
        <header className="integrity-baseline-header"><div><h1>Régularisation des empreintes</h1><p>Établir une référence SHA-256 pour les versions anciennes qui n’en possèdent pas.</p></div></header>
        {!isAdmin ? <p role="alert">Accès réservé aux administrateurs.</p> : <>
          <section className="integrity-baseline-card">
            <div className="integrity-baseline-top"><div><strong>{loading ? '…' : overview.pending}</strong><span>version(s) sans empreinte</span></div><p>Les empreintes existantes ne sont jamais remplacées. Une relance vérifie les versions déjà régularisées et traite uniquement celles encore sans empreinte. Les écarts et les fichiers absents ou illisibles sont signalés.</p></div>
            <div className="integrity-baseline-actions">
              <button className="btn btn-primary" type="button" onClick={currentRun ? () => processRun(currentRun.id) : start} disabled={loading || working}>{working ? 'Traitement en cours…' : currentRun ? 'Reprendre la campagne' : overview.pending ? 'Lancer la campagne' : 'Vérifier les empreintes'}</button>
              {currentRun && !working && <span>Campagne interrompue : reprise au dernier lot enregistré.</span>}
              {working && <span>Gardez cet écran ouvert pendant le traitement. Vous pourrez reprendre la campagne si elle est interrompue.</span>}
            </div>
            {selectedRun && <div className="integrity-baseline-progress" role="status"><strong>{selectedRun.status === 'running' ? 'En cours' : 'Terminée'}</strong><span>{selectedRun.processedCount} empreinte(s) créée(s) · {selectedRun.verifiedCount} vérifiée(s) · {selectedRun.issueCount} exception(s) · {selectedRun.totalCount} version(s) parcourue(s)</span></div>}
            {error && <p className="integrity-baseline-error" role="alert">{error}</p>}
          </section>
          <section className="integrity-baseline-card"><h2>Campagnes récentes</h2>
            {overview.runs.length === 0 ? <p>Aucune campagne lancée.</p> : <div className="table-responsive"><table className="table syidocs-data-table"><thead><tr><th>Début</th><th>État</th><th>Empreintes</th><th>Exceptions</th><th>Rapport</th></tr></thead><tbody>{overview.runs.map((run) => <tr key={run.id}><td>{formatDate(run.startedAt)}</td><td>{run.status === 'running' ? 'En cours' : 'Terminée'}</td><td>{run.processedCount}</td><td>{run.issueCount}</td><td><button className="btn btn-outline-primary btn-sm" type="button" onClick={() => showRun(run.id)}>Voir</button></td></tr>)}</tbody></table></div>}
          </section>
          {selectedRun && <section className="integrity-baseline-card"><div className="integrity-baseline-report-title"><h2>Rapport de la campagne</h2><button className="btn btn-outline-primary btn-file-action" type="button" onClick={() => exportRun(selectedRun.id)}>Exporter CSV</button></div><p>Identifiant : <code>{selectedRun.id}</code> · Début : {formatDate(selectedRun.startedAt)} · Fin : {formatDate(selectedRun.completedAt)}</p><p>Les empreintes créées sont journalisées sur chaque archive avec leur version et la mention « référence établie a posteriori ».</p><h3>Exceptions</h3>{issues.length === 0 ? <p>Aucune exception enregistrée.</p> : <div className="table-responsive"><table className="table syidocs-data-table"><thead><tr><th>Archive</th><th>Version</th><th>Motif</th><th>Contrôlé le</th></tr></thead><tbody>{issues.map((issue) => <tr key={issue.versionId}><td>{issue.documentId}</td><td>{issue.versionId}</td><td>{issue.message}</td><td>{formatDate(issue.checkedAt)}</td></tr>)}</tbody></table></div>}{selectedRun.issueCount > issues.length && <p>Seules les 500 premières exceptions sont affichées ici. Le CSV contient le rapport complet.</p>}</section>}
        </>}
      </main>
    </div></div></div>
  </>;
}
